/**
 * An interactive procedural animation derived from
 * a C0 continuous Bézier spline.
 *
 * Based on ideas from
 * "A simple procedural animation technique" by argonaut
 * and "The Beauty of Bézier Curves" by Freya Holmér.
 *
 * https://www.youtube.com/watch?v=qlfh_rv6khY
 * https://www.youtube.com/watch?v=aVwxzDHniEw
 * 
 * A lot of the code here would appear repetitive.
 * That's because the math needed to keep the canvas'
 * dimensions responsive is complicated enough,
 * and abstraction would just make the code hard to
 * sift through.
 * DRY has a time and place, this is not it.
 */
import "./suppress-hydration-warning.ts"
import "./Nameplay.css"

/**
 * Hand-drawn on paper, traced in illustrator, exported as SVG with a viewBox of "0 0 100 40"
 */
export const d = "M.98,35c3,1.38,4.99-9.98,12.6-15.71,8.24-6.2,7.13.21-.26,3.65-12.54,5.83-3.42,17.52,2,10.41,10.46-13.7-4.76-19.4,5.67-.98,8.08,14.25,18.56-15.97,10.87-17.6-2.96-.63-6.28,7.05-.63,6.9,8.62-.22,11.42-1.24,11,5.03-.93,13.99,2.45,11.28,9.01,6.09,5.13-4.06,8.15-10.46,8.7-17.36.75-9.41-9.43,1.36-4.57,4.13,5.42,3.08,12.56,7.1,10.32,12.26-3.09,7.13-13.77,5.77-6.73.74,3.57-2.55,6.67-2.18,11.88-7.39,7.46-7.44,6.81-11.57,5.49-17.85-3.34-15.78-8.93-3.1-5.08,10.37,8.85,30.95-5.63,15.47,7.65,3.8,7.71-6.78,6.33-.15,6.26,3.19-.3,13.83,11.39,10.27,13.6-4.79"

/**
 * The number of points to draw the chain with.
 */
const resolution = 400

// Indexing into an array is faster than retrieving fields of an object
type Padding = [top: number, right: number, bottom: number, left: number]
const padding: Padding = [20, 30, 20, 30]

const top = 0 as const
const right = 1 as const
const bottom = 2 as const
const left = 3 as const

export default function () {
    return <arsh role="img" aria-label="Arsh - Interactive Logo">
        {import.meta.server ? <svg viewBox="0 0 100 40">
            <path d={d} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg> :
            <>
                <interaction-indicator />
                <canvas aria-hidden ref={bootstrapCanvas} data-hi="Looking for the source code? Open the sources tab in devtools and look for components/Nameplay.tsx 😉" />
            </>
        }
    </arsh>
}

declare module "react" {
    namespace JSX {
        interface IntrinsicElements {
            "arsh": React.DetailedHTMLProps<React.HTMLAttributes<HTMLUnknownElement>, HTMLUnknownElement>
            "interaction-indicator": React.DetailedHTMLProps<React.HTMLAttributes<HTMLUnknownElement>, HTMLUnknownElement>
        }
    }
}

function bootstrapCanvas(canvas: HTMLCanvasElement): () => void {
    const { style: indicatorStyle } = canvas.previousElementSibling as HTMLDivElement
    const ctx = canvas.getContext("2d", { colorSpace: "display-p3" })!

    const isTouchDevice = window.matchMedia("(hover: none)").matches

    /**
     * The path has co-ordinates in the range 0-100.
     * To draw a canvas of a size wider than 100px, we scale up
     * the co-ordinates and other constants.
     */
    let scale = canvas.clientWidth / (100 + padding[left] + padding[right])
    /**
     * To keep the text centered while the canvas changes aspect ratios
     * across different screens, we add offsets when rendering.
     * 
     * The offsets are using absolute pixel units, after devicePixelRatio
     * has been applied.
     */
    let offsetY = 0
    let offsetX = 0
    canvas.width = scale * (100 + padding[left] + padding[right])
    canvas.height = scale * (40 + padding[top] + padding[bottom])

    const path = document.createElementNS("http://www.w3.org/2000/svg", "path")
    path.setAttribute("d", d)
    const totalLength = path.getTotalLength()

    let segmentLength = totalLength / resolution

    /**
     * Structure of arrays to optimize memory lookups.
     *
     * In V8, some numbers in arrays can be unboxed,
     * resulting in even faster memory lookups. The
     * optimization only applies to numbers that can
     * be represented as a 31-bit integer, not floats.
     *
     * `Float64Array` theoretically has the optimal
     * memory layout, but empirically it's about 25%
     * slower than `number[]` for r/w on Chromium and
     * WebKit.
     */
    const alpX: number[] = []
    const alpY: number[] = []

    for (let i = 1; i <= resolution; i++) {
        /**
         * We generate points in reverse order (starting from the
         * letter h, ending at the letter a) because propelChain
         * needs to run from right to left. Reading an array in
         * reverse would be inefficeint due to cpu cache misses,
         * so we write in reverse instead.
         */
        const nth = resolution - i
        /**
         * We use getPointAtLength (built right into browsers!) for
         * arc-length parameterisation.
         */
        const point = path.getPointAtLength((nth / (resolution - 1)) * totalLength)
        alpX.push(point.x)
        alpY.push(point.y)
    }

    /**
     * These mutable copies of alpX and alpY are used to
     * update and render the chain.
     */
    const posX: number[] = alpX.map(x => x)
    const posY: number[] = alpY.map(y => y)

    render(canvas, ctx, posX, posY, offsetX, offsetY, scale, padding)

    indicatorStyle.setProperty("--x", `${scale * (posX[0]! + padding[left])}px`)
    indicatorStyle.setProperty("--y", `${scale * (posY[0]! + padding[top])}px`)
    indicatorStyle.setProperty("--size", `${8 * scale}px`)

    const eventListenerOptions = { passive: true }

    // Add appropriate event listeners based on device type
    if (isTouchDevice) {
        canvas.addEventListener("touchmove", pullChain, eventListenerOptions)
        canvas.addEventListener("touchend", restoreChain, eventListenerOptions)
        canvas.addEventListener("touchcancel", restoreChain, eventListenerOptions)
    } else {
        canvas.addEventListener("mousemove", pullChain, eventListenerOptions)
        canvas.addEventListener("click", restoreChain, eventListenerOptions)
        canvas.addEventListener("mouseleave", restoreChain, eventListenerOptions)
    }

    const resizeObserver = new ResizeObserver(onResize)
    resizeObserver.observe(canvas.parentElement!)

    return () => {
        if (isTouchDevice) {
            canvas.removeEventListener("touchmove", pullChain)
            canvas.removeEventListener("touchend", restoreChain)
            canvas.removeEventListener("touchcancel", restoreChain)
        } else {
            canvas.removeEventListener("mousemove", pullChain)
            canvas.removeEventListener("click", restoreChain)
            canvas.removeEventListener("mouseleave", restoreChain)
        }
        resizeObserver.disconnect()
    }

    function onResize([ entry ]: ResizeObserverEntry[]) {
        if (entry === undefined) return
        const [ size ] = entry.contentBoxSize
        if (size === undefined) return
        const { inlineSize, blockSize } = size
        canvas.width = devicePixelRatio * inlineSize
        canvas.height = devicePixelRatio * blockSize
        const innerWidthAbstract = 100 + padding[left] + padding[right]
        const innerHeightAbstract = 40 + padding[top] + padding[bottom]
        const aspectRatio = innerWidthAbstract / innerHeightAbstract
        const innerWidth = Math.min(inlineSize, blockSize * aspectRatio)
        const innerHeight = Math.min(blockSize, inlineSize / aspectRatio)
        scale = Math.min(innerWidth / innerWidthAbstract, innerHeight / innerHeightAbstract)
        offsetX = (inlineSize - innerWidth) / (2 * scale)
        offsetY = (blockSize - innerHeight) / (2 * scale)
        segmentLength = totalLength / resolution
        render(canvas, ctx, posX, posY, offsetX, offsetY, scale, padding)
        indicatorStyle.setProperty("--x", `${scale * (offsetX + padding[left] + posX[0]!)}px`)
        indicatorStyle.setProperty("--y", `${scale * (offsetY + padding[top] + posY[0]!)}px`)
        indicatorStyle.setProperty("--size", `${8 * scale}px`)
    }

    function pullChain(event: MouseEvent | TouchEvent) {
        /**
         * On Firefox desktop, TouchEvent is undefined.
         */
        const isTouch = typeof TouchEvent !== "undefined" && event instanceof TouchEvent
        const x = isTouch ? event.touches[0]!.clientX : (event as MouseEvent).clientX
        const y = isTouch ? event.touches[0]!.clientY : (event as MouseEvent).clientY

        const rect = canvas.getBoundingClientRect()
        posX[0] = (x - rect.left - scale * (offsetX + padding[left])) / scale
        posY[0] = (y - rect.top - scale * (offsetY + padding[top])) / scale

        for (let segment = 1; segment < resolution; segment++) {
            follow(posX, posY, segment, posX[segment - 1]!, posY[segment - 1]!, segmentLength)
        }

        render(canvas, ctx, posX, posY, offsetX, offsetY, scale, padding)
        indicatorStyle.setProperty("--x", `${scale * (offsetX + padding[left] + posX[0]!)}px`)
        indicatorStyle.setProperty("--y", `${scale * (offsetY + padding[top] + posY[0]!)}px`)
        indicatorStyle.setProperty("--size", `${8 * scale}px`)
        indicatorStyle.setProperty("animation", "none")
    }

    function restoreChain() {
        /**
         * The number of points that have moved significantly.
         * 
         * Naively restoring the chain will result in the
         * animation running over segments that haven't
         * apparently (they always move at least a little)
         * moved.
         *
         * That could be confusing, and also underwhelming
         * since nothing happens during the first few parts.
         *
         * So, we find the first segment that has moved
         * significantly and start the animation from there.
         */
        let movedPoints = 0
        for (let i = resolution - 1; i >= 0; i--) {
            const distance = Math.sqrt(Math.pow(alpX[i]! - posX[i]!, 2) + Math.pow(alpY[i]! - posY[i]!, 2))
            if (distance > 3) {
                movedPoints = i
                break
            }
        }
        if (movedPoints === 0) {
            return
        }
        /**
         * At most 3 seconds, but proportional to the length
         * of the chain that actually needs restoration.
         * 
         * `movedPoints` may be at most `resolution`, the number
         * of total points in the chain.
         */
        const totalDuration = (movedPoints / resolution) * 3000

        /**
         * The number of segments that have been restored to their original position.
         */
        let restoredCount = 0

        /**
         * Before starting the animation, we remove event
         * listeners to prevent the chain from getting
         * hooked onto the cursor mid-animation. It also
         * prevents the animation from running twice.
         */
        if (isTouchDevice) {
            canvas.removeEventListener("touchmove", pullChain)
            canvas.removeEventListener("touchend", restoreChain)
            canvas.removeEventListener("touchcancel", restoreChain)
        } else {
            canvas.removeEventListener("mousemove", pullChain)
            canvas.removeEventListener("click", restoreChain)
            canvas.removeEventListener("mouseleave", restoreChain)
        }

        let startingTimestamp: number | undefined = undefined

        /**
         * To prevent timing artifacts, we shouldn't depend
         * on the number of times `animate` gets called.
         *
         * Consumer displays can be upto 240Hz, low-end devices
         * may run on a lower than normal framerate, or even
         * skip frames.
         *
         * Instead, we determine how far along the animation
         * has progressed based on the timestamps provided
         * by `requestAnimationFrame`.
         */
        function animate(timestamp: number) {
            if (startingTimestamp === undefined) {
                startingTimestamp = timestamp
                return requestAnimationFrame(animate)
            }
            const animationProgress = (timestamp - startingTimestamp) / totalDuration
            const skippedPortion = (resolution - movedPoints) / resolution
            /**
             * The number of segments that should be restored by this point in the animation.
             */
            const restoreCount = Math.floor((skippedPortion + (animationProgress * (1 - skippedPortion))) * resolution)
            /**
             * Restoration is done in a for loop because we can't rely
             * on `requestAnimationFrame` having been called for each one.
             */
            for (let segment = resolution - restoreCount; segment < resolution - restoredCount; segment++) {
                posX[segment] = alpX[segment]!
                posY[segment] = alpY[segment]!
            }
            restoredCount = restoreCount
            for (let segment = resolution - restoreCount - 1; segment >= 0; segment--) {
                follow(posX, posY, segment, posX[segment + 1]!, posY[segment + 1]!, segmentLength)
            }
            render(canvas, ctx, posX, posY, offsetX, offsetY, scale, padding)
            if (animationProgress < 1) {
                indicatorStyle.setProperty("--x", `${scale * (offsetX + padding[left] + posX[resolution - restoreCount]!)}px`)
                indicatorStyle.setProperty("--y", `${scale * (offsetY + padding[top] + posY[resolution - restoreCount]!)}px`)
                indicatorStyle.setProperty("animation", "none")
                requestAnimationFrame(animate)
            } else {
                if (isTouchDevice) {
                    canvas.addEventListener("touchmove", pullChain, eventListenerOptions)
                    canvas.addEventListener("touchend", restoreChain, eventListenerOptions)
                    canvas.addEventListener("touchcancel", restoreChain, eventListenerOptions)
                } else {
                    canvas.addEventListener("mousemove", pullChain, eventListenerOptions)
                    canvas.addEventListener("click", restoreChain, eventListenerOptions)
                    canvas.addEventListener("mouseleave", restoreChain, eventListenerOptions)
                }
                indicatorStyle.setProperty("--x", `${scale * (offsetX + padding[left] + posX[0]!)}px`)
                indicatorStyle.setProperty("--y", `${scale * (offsetY + padding[top] + posY[0]!)}px`)
                indicatorStyle.removeProperty("animation")
            }
        }
        requestAnimationFrame(animate)
    }
}

/**
 * Makes a segment follow another segment.
 * At the end of the this function, the segment at `followerIndex`
 * will be `segmentLength` away from the `[ followX, followY ]`.
 * 
 * This is expected to run once for every segment per frame.
 */
function follow(posX: number[], posY: number[], followerIndex: number, followX: number, followY: number, segmentLength: number) {
    const dx = followX - posX[followerIndex]!
    const dy = followY - posY[followerIndex]!
    const angle = Math.atan2(dy, dx)

    posX[followerIndex] = followX - Math.cos(angle) * segmentLength
    posY[followerIndex] = followY - Math.sin(angle) * segmentLength
}

function render(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D, posX: number[], posY: number[], offsetX: number, offsetY: number, scale: number, padding: [number, number, number, number]) {
    const scalingFactor = devicePixelRatio * scale
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.beginPath()
    ctx.moveTo(
        scalingFactor * (offsetX + padding[left] + posX[0]!),
        scalingFactor * (offsetY + padding[top] + posY[0]!)
    )

    for (let segment = 1; segment < resolution - 1; segment++) {
        const xc = (posX[segment]! + posX[segment + 1]!) / 2
        const yc = (posY[segment]! + posY[segment + 1]!) / 2
        /**
         * Using lineTo results in artifacts around sharp 180 degree turns.
         * quadraticCurveTo appears much cleaner.
         */
        ctx.quadraticCurveTo(
            scalingFactor * (offsetX + padding[left] + posX[segment]!),
            scalingFactor * (offsetY + padding[top] + posY[segment]!),
            scalingFactor * (offsetX + padding[left] + xc),
            scalingFactor * (offsetY + padding[top] + yc)
        )
    }

    ctx.strokeStyle = getComputedStyle(canvas).color || "#ff9800"
    ctx.lineWidth = 1.5 * scalingFactor
    ctx.lineCap = "round"
    ctx.stroke()
}
